<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { page } from '$stores/pageStore';
  import PopupForm from './persons/PersonForm.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties, isFeatureEnabled, fetchStudyProperties } from '$stores/configStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '../lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faChevronLeft,
    faChevronRight,
    faSquarePlus,
    faTrashCan,
    faPencil,
    faRotate,
    faSearch,
    faCircleXmark,
    faHistory,
    faPlus,
    faCopy,
    faEllipsis
  } from '@fortawesome/pro-light-svg-icons';
  import { formatToLocalDateTime, isWithinLastHours, isOlderThanHours, getCurrentDateInTimezone } from '$utils/dateUtils.js';
  import { user, userRole, userRoles, userReadOnly } from '$stores/sessionStore';
  import { hasAuditLogAccess, hasAccessUiAction } from '$utils/permissionUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  const browser = typeof window !== 'undefined';
  // TODO: replace SvelteKit `error` — throw a plain Error or surface via a store
  import PatientsInfoCards from '$lib/PatientsInfoCards.svelte';
  import SiteForm from './sites/SiteForm.svelte';
  

  // Initial state defaults (folded from former +page.server.js):
  //   persons: [], resultCount: 0, currentPage: 1, totalPages: 1
  // These are already the declared defaults below.

  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  let persons = [];
  let allPersons = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let searchText = '';
  let isExporting = false;
  let hasError = false;
  let errorMessage = '';

  let show = false;
  let showForm = false;
  let showConfirmation = false;
  let showAuditLog = false;
  let editPerson = null;
  let personToDelete = null;
  let auditLogPerson = null;
  let studyId = null;
  let siteId = null;
  let currentFetch = null;
  let isInitialized = false;
  let editSite = null;
  let auditLogSite = null;
  let showSiteForm = false;
  let showSiteAuditLog = false;

  let showPersonMenu = false;
  let selectedPersonForMenu = null;
  let personMenuPosition = { top: 0, left: 0, direction: 'down' };

  let showSiteAssignmentModal = false;
  let personToAssignSite = null;
  let studySites = [];
  let studySitesStudyId = null;
  let isStudySitesLoading = false;
  let studySitesError = '';
  let selectedSiteForAssignment = '';
  let siteAssignmentConfirmText = '';
  let isSiteAssignmentSaving = false;
  let siteAssignmentError = '';

  // Feature flag for participants card view
  let isParticipantsCardViewEnabled = true;

  // Reactive variable for feature flags
  $: {
    isParticipantsCardViewEnabled = isFeatureEnabled('cardViews.participants');
  }

  $: isUpgradeEligibilityEnabled = isFeatureEnabled('upgradeEligibility');
  $: canToggleEligibility = hasAccessUiAction(
    'participants.persons.toggleEligibility',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  // Subscribe to selectedStudy changes to fetch properties
  $: if ($selectedStudy?._id) {
    fetchStudyProperties($selectedStudy._id);
  } else {
    studyProperties.set(null);
  }

  // Add cleanup on component destroy
  onDestroy(() => {
    if (currentFetch) {
      currentFetch.abort();
    }
  });

  // Re-fetch participants only when the SITE ID changes — not when other
  // fields on $selectedSite change (e.g., user edits the site's timezone or
  // status). Without the id-track guard, every store mutation triggers a
  // full participants reload that returns the same list (wasted bandwidth +
  // momentary blank-render).
  let lastFetchedSiteId = null;
  $: if (browser && isInitialized && $selectedSite?._id && $selectedSite._id !== lastFetchedSiteId) {
    lastFetchedSiteId = $selectedSite._id;
    fetchPersons(currentPage).catch(err => {
      console.error('Error in store subscription:', err);
      hasError = true;
      errorMessage = err.message;
    });
    fetchAllPersons().catch(err => {
      console.error('Error fetching all persons:', err);
    });
  }

  onMount(async () => {
    if (!browser) return;  // Don't run on server

    try {
      //console.log('Page mounting, selectedSite:', $selectedSite);
      
      selectedParticipant.set(null);

      // Get search text from URL parameter if it exists
      const urlSearchText = $page.url.searchParams.get('search');
      if (urlSearchText) {
        searchText = urlSearchText;
      }

      // Get page number from URL parameter if it exists
      const urlPage = parseInt($page.url.searchParams.get('page')) || 1;
      currentPage = urlPage;

      if (!$selectedSite) {
        //console.log('No site selected, redirecting to site selection');
        goto('/sites');
        return;
      }

      // Mark as initialized after initial setup
      isInitialized = true;
    } catch (err) {
      console.error('Error in onMount:', err);
      hasError = true;
      errorMessage = err.message;
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  // Create an abort controller factory
  function createAbortController() {
    if (currentFetch) {
      currentFetch.abort();
    }
    return new AbortController();
  }

  async function fetchPersons(page = 1) {
    const requestId = Date.now();  // Add unique ID for this request
    //console.log(`Starting fetch request ${requestId}`);

    // Create a new abort controller for this request
    const abortController = createAbortController();
    currentFetch = abortController;

    if (!$selectedSite || !$selectedSite._id) {
        console.warn(`[Request ${requestId}] No site selected or invalid site data`);
        persons = [];
        resultCount = 0;
        currentPage = 1;
        totalPages = 1;
        return;
    }

    if (!API_BASE_URL) {
        console.error(`[Request ${requestId}] Missing API configuration`);
        return;
    }

    try {
        isLoading = true;
        const url = `${API_BASE_URL}/persons?page=${page}&pageSize=${limit}&searchText=${searchText}&sortOrder=asc&siteGUID=${$selectedSite._id}`;
        
        const res = await fetch(url, {
            signal: abortController.signal
        });

        // Check if this is still the current fetch
        if (currentFetch !== abortController) {
            console.log(`[Request ${requestId}] Fetch was superseded by newer request`);
            return;
        }

        if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            console.error(`[Request ${requestId}] API Error Response:`, {
                status: res.status,
                statusText: res.statusText,
                errorData
            });
            throw new Error(`Failed to fetch persons: ${res.status} ${errorData.message || ''}`);
        }

        const responseData = await res.json();

        // Only update state if this is still the current fetch
        if (currentFetch === abortController) {
            resultCount = responseData.totalCount || 0;
            currentPage = responseData.page || 1;
            totalPages = responseData.totalPages || 1;
            persons = responseData.data;
        }

    } catch (err) {
        if (err.name === 'AbortError') {
            console.log(`[Request ${requestId}] Fetch was aborted`);
            return;
        }
        console.error(`[Request ${requestId}] Error fetching persons:`, err);
        persons = [];
        resultCount = 0;
        currentPage = 1;
        totalPages = 1;
    } finally {
        if (currentFetch === abortController) {
            isLoading = false;
            currentFetch = null;
        }
    }
  }

  async function handleCreatePerson(event) {
    const newPerson = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/persons`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPerson),
      });

      if (!res.ok) {
        throw new Error('Failed to create person');
      }

      // Get the newly created person data
      const response = await res.json();
      console.log('Person creation response:', response);
      
      // Extract the ID from the nested response structure
      // NG backend returns the full enriched person object in `data` (with `_id`).
      // Legacy returns the Mongo driver's `{insertedId}` shape. Accept either.
      const personId = response.data?.insertedId || response.data?._id;
      
      if (!personId) {
        console.error('Created person response is missing ID:', response);
        throw new Error('Invalid person data received from server');
      }
      
      console.log('Created person with ID:', personId);
      
      // Fetch the complete person data since the creation response only has the ID
      try {
        const personRes = await fetch(`${API_BASE_URL}/persons/${personId}`);
        
        if (!personRes.ok) {
          throw new Error('Failed to fetch created person data');
        }
        
        const fullPersonData = await personRes.json();
        console.log('Fetched full person data:', fullPersonData);
        
        // Set the newly created person as the selected participant
        selectedParticipant.set(fullPersonData);
        
        // Generate schedule for the newly created person
        if ($selectedStudy) {
          try {
            // Use the participant's timezone for the start date
            const participantTimezone = fullPersonData.timezoneCode || 'UTC';
            console.log('participantTimezone:', participantTimezone);
            const startDate = getCurrentDateInTimezone(participantTimezone);
            console.log('startDate:', startDate);
            
            const scheduleRes = await fetch(`${API_BASE_URL}/subjectSchedule/generate-subject-schedule`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                subjectGUID: personId,
                studyGUID: $selectedStudy._id,
                startDate: startDate.toISOString()
              })
            });
            
            if (!scheduleRes.ok) {
              const errorData = await scheduleRes.json().catch(() => ({}));
              console.error('Failed to generate schedule for new participant:', errorData.message || 'Unknown error');
            } else {
              const scheduleData = await scheduleRes.json();
              console.log('Schedule generated successfully:', scheduleData);
            }
          } catch (scheduleErr) {
            console.error('Error generating schedule:', scheduleErr);
          }
        }

        // Navigate to the person detail page
        goto(`/schedule?id=${personId}`);
      } catch (fetchErr) {
        console.error('Error fetching complete person data:', fetchErr);
      }
      
      // We don't need to fetch persons or close the form since we're navigating away
    } catch (err) {
      console.error('Error creating person:', err);
      await fetchPersons();
      showForm = false;
    }
  }

  async function handleEditPerson(event) {
    const updatedPerson = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/persons/${updatedPerson._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedPerson),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update person');
      }

      await fetchPersons();
      await fetchAllPersons();
      showForm = false;
      editPerson = null;
    } catch (err) {
      console.error('Error updating person:', err);
    }
  }

  async function confirmDelete() {
    if (personToDelete) {
      try {
        const res = await fetch(`${API_BASE_URL}/persons/${personToDelete._id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
        });

        if (!res.ok) {
          throw new Error('Failed to delete person');
        }

        const data = await res.json();

        await fetchPersons();
        await fetchAllPersons();
        showConfirmation = false;
        personToDelete = null;
      } catch (err) {
        console.error('Error deleting person:', err);
      }
    }
  }

  function handleDeleteClick(person) {
    personToDelete = person;
    showConfirmation = true;
  }

  function handleAuditLogClick(person) {
    auditLogPerson = person;
    showAuditLog = true;
  }

  async function handleEditClick(person) {
    try {
      const res = await fetch(`${API_BASE_URL}/persons/${person._id}`);
      if (res.ok) {
        const fullPerson = await res.json();
        editPerson = fullPerson;
      } else {
        // Fallback to the provided person if detail fetch fails
        editPerson = person;
        console.error('Failed to fetch full person for edit:', res.status, await res.text().catch(() => ''));
      }
    } catch (err) {
      // Fallback on network or other errors
      editPerson = person;
      console.error('Error fetching full person for edit:', err);
    } finally {
      showForm = true;
    }
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      // Update URL with page number
      const url = new URL(window.location);
      url.searchParams.set('page', newPage.toString());
      goto(url.toString(), { replaceState: true });

      fetchPersons(newPage);
    }
  }

  function handleNewClick() {
    studyId = $selectedStudy ? $selectedStudy.studyId : 'NONE';
    siteId = $selectedSite ? $selectedSite.siteId : 'NONE';
    editPerson = null;
    showForm = true;
  }

  function handlePersonClick(person) {
    selectedParticipant.set(person);
    goto(`/person`);
  }

  function handleRefresh() {
    fetchPersons(1);
    fetchAllPersons();
  }

  function handleSearch() {
    // Update URL with search text and reset page to 1
    const url = new URL(window.location);
    if (searchText) {
      url.searchParams.set('search', searchText);
    } else {
      url.searchParams.delete('search');
    }
    url.searchParams.set('page', '1'); // Reset to page 1 when searching
    goto(url.toString(), { replaceState: true });
    fetchPersons(1);
    fetchAllPersons();
  }

  function handleClearSearch() {
    searchText = '';
    // Reset page to 1 when clearing search
    currentPage = 1;
    handleSearch();
  }

  // Add computed property for edit permissions using the centralized hasRole function
  $: canAddPersons = hasAccessUiAction(
    'participants.persons.add',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canEditPersons = hasAccessUiAction(
    'participants.persons.edit',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canDeletePersons = hasAccessUiAction(
    'participants.persons.delete',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canAssignParticipantSite = hasAccessUiAction(
    'participants.persons.assignSite',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);

  // KPI-card loading flag — true while the bulk fetch is in flight, false on
  // both success and error. Avoids the misleading "0" flash during boot.
  let isLoadingKpi = true;

  async function fetchAllPersons() {
    if (!$selectedSite || !$selectedSite._id) {
      allPersons = [];
      isLoadingKpi = false;
      return;
    }

    isLoadingKpi = true;
    try {
      const url = `${API_BASE_URL}/persons?page=1&pageSize=999999&searchText=${searchText}&sortOrder=asc&siteGUID=${$selectedSite._id}`;
      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch all persons');
      }

      const responseData = await res.json();
      allPersons = responseData.data || [];
    } catch (err) {
      console.error('Error fetching all persons:', err);
      allPersons = [];
    } finally {
      isLoadingKpi = false;
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      
      // Fetch all persons data directly for export
      if (!$selectedSite || !$selectedSite._id) {
        throw new Error('No site selected');
      }

      const url = `${API_BASE_URL}/persons?page=1&pageSize=999999&searchText=${searchText}&sortOrder=asc&siteGUID=${$selectedSite._id}`;
      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch persons data for export');
      }

      const responseData = await res.json();
      const allPersons = responseData.data || [];

      const now = new Date();
      const dateStr = now
        .toLocaleString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        .replace(/,/g, '')
        .replace(/\s+/g, '')
        .replace(/:/g, '')
        .replace(/(\d{2})([A-Za-z]{3})(\d{4})_(\d{2})(\d{2})/, '$2$1$3_$4$5');

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        siteId: $selectedSite?.siteId,
        reportType: 'Participants'
      });

      const formattedData = allPersons.map((person) => ({
        Protocol: $selectedStudy?.protocolNumber || '',
        Site: $selectedSite?.siteId || '',
        Participant: person.subjectId || '',
        Device: person.properties?.enabledDevice || 'Any',
        'Latest Reading (Local)': person.latestReadingDate
          ? formatToLocalDateTime(person.latestReadingDate, person.timezoneCode)
          : '',
        Language: person.language ? `${person.flag} ${person.language}` : '',
        Timezone: person.timezoneCode || '',
        Blinded: person.blinded ? 'Y' : 'N',
        Status: person.statusLabel || '',
        'Device Manufacturer': person.glucoseManufacturer || '',
        'Device Model': person.glucoseModel || '',
      }));

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }

  async function handleEditSite(event) {
    const updatedSite = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/sites/${updatedSite.siteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedSite),
      });

      if (!res.ok) {
        throw new Error('Failed to update site');
      }

      // Use the enriched save response (has timezoneCode/timezoneLabel from
      // the backend aggregation), not the raw form input which only carries
      // timezoneGUID — otherwise SiteDetails' Local Time formatter keeps
      // using the pre-edit timezone.
      const responseBody = await res.json();
      selectedSite.set(responseBody?.data ?? updatedSite);
      showSiteForm = false;
      editSite = null;
    } catch (err) {
      console.error('Error updating site:', err);
    }
  }

  function getSiteLabel(site) {
    if (!site) return '';
    const name = site.siteName || site.name || site.siteId || site._id || '';
    const id = site.siteId || site._id || '';
    if (name && id && name !== id) {
      return `${name} (${id})`;
    }
    return name || id || 'Unknown site';
  }

  async function fetchStudySites() {
    if (!$selectedStudy?._id) {
      studySites = [];
      return;
    }

    if (studySitesStudyId === $selectedStudy._id && studySites.length > 0) {
      return;
    }

    isStudySitesLoading = true;
    studySitesError = '';

    try {
      const url = `${API_BASE_URL}/sites?page=1&pageSize=999999&searchText=&sortOrder=asc&studyGUID=${$selectedStudy._id}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error('Failed to fetch study sites');
      }

      const data = await res.json();
      const sitesData = Array.isArray(data) ? data : data.data || [];

      if (sitesData.length === 0) {
        const alternativeUrl = `${API_BASE_URL}/sites/study/${$selectedStudy._id}`;
        const altRes = await fetch(alternativeUrl);
        if (altRes.ok) {
          const altData = await altRes.json();
          studySites = Array.isArray(altData) ? altData : altData.data || [];
        } else {
          studySites = [];
        }
      } else {
        studySites = sitesData;
      }

      studySitesStudyId = $selectedStudy._id;
    } catch (err) {
      console.error('Error fetching study sites:', err);
      studySitesError = err.message || 'Failed to load study sites';
      studySites = [];
    } finally {
      isStudySitesLoading = false;
    }
  }

  function openSiteAssignmentModal(person) {
    personToAssignSite = person;
    selectedSiteForAssignment = person?.siteGUID || $selectedSite?._id || '';
    siteAssignmentConfirmText = '';
    siteAssignmentError = '';
    showSiteAssignmentModal = true;
    fetchStudySites();
  }

  function closeSiteAssignmentModal() {
    showSiteAssignmentModal = false;
    personToAssignSite = null;
    selectedSiteForAssignment = '';
    siteAssignmentConfirmText = '';
    siteAssignmentError = '';
  }

  async function confirmSiteAssignmentChange() {
    if (!personToAssignSite?._id) return;

    if (!selectedSiteForAssignment || selectedSiteForAssignment === personToAssignSite.siteGUID) {
      siteAssignmentError = 'Select a different site to continue.';
      return;
    }

    if (siteAssignmentConfirmText.trim() !== 'transfer') {
      siteAssignmentError = 'Please type "transfer" to confirm.';
      return;
    }

    isSiteAssignmentSaving = true;
    siteAssignmentError = '';

    try {
      const personRes = await fetch(`${API_BASE_URL}/persons/${personToAssignSite._id}`);
      if (!personRes.ok) {
        throw new Error('Failed to load participant details');
      }

      const fullPerson = await personRes.json();
      const updatedPerson = {
        ...fullPerson,
        siteGUID: selectedSiteForAssignment
      };

      const updateRes = await fetch(`${API_BASE_URL}/persons/${personToAssignSite._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedPerson)
      });

      if (!updateRes.ok) {
        const errorData = await updateRes.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update participant site');
      }

      await fetchPersons(currentPage);
      await fetchAllPersons();
      closeSiteAssignmentModal();
    } catch (err) {
      console.error('Error updating participant site:', err);
      siteAssignmentError = err.message || 'Failed to update participant site';
    } finally {
      isSiteAssignmentSaving = false;
    }
  }

  function handlePersonMenuClick(event, person) {
    event.stopPropagation();
    selectedPersonForMenu = person;
    // Get button position
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 180; // Approximate visible height
    const menuWidth = 192; // w-48 in px
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    let direction = 'down';
    // For a fixed-positioned menu, use viewport coordinates directly
    let top = buttonRect.bottom;
    let left = buttonRect.left;
    // If not enough space below, flip up and clamp to top
    if (buttonRect.bottom + menuHeight > viewportHeight) {
      direction = 'up';
      top = Math.max(0, buttonRect.top - menuHeight);
    }
    // Prevent menu from overflowing right edge
    left = Math.min(left, viewportWidth - menuWidth - 8);
    left = Math.max(0, left);
    personMenuPosition = { top, left, direction };
    showPersonMenu = true;
  }

  function handlePersonMenuClose() {
    showPersonMenu = false;
    selectedPersonForMenu = null;
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showPersonMenu && !event.target.closest('.menu-container')) {
      handlePersonMenuClose();
    }
  }
</script>

<PopupForm
  {showForm}
  {editPerson}
  on:submit={editPerson ? handleEditPerson : handleCreatePerson}
  on:close={() => {
    showForm = false;
    editPerson = null;
  }}
/>

<ConfirmationModal
  {showConfirmation}
  message="Are you sure you want to delete this participant?"
  on:confirm={confirmDelete}
  on:close={() => (showConfirmation = false)}
/>

{#if showSiteAssignmentModal}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Transfer participant site</h2>
        <button
          type="button"
          on:click={closeSiteAssignmentModal}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Close"
        >
          &times;
        </button>
      </div>
      <div class="p-5 space-y-4">
        <div class="space-y-1">
          <div class="text-sm text-gray-700 dark:text-gray-200">
            Participant: {personToAssignSite?.firstName || ''} {personToAssignSite?.lastName || ''}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400">
            Current site: {getSiteLabel(studySites.find(site => site._id === personToAssignSite?.siteGUID))}
          </div>
        </div>

        {#if isStudySitesLoading}
          <div class="text-sm text-gray-500 dark:text-gray-400">Loading study sites...</div>
        {:else if studySitesError}
          <div class="text-sm text-red-600 dark:text-red-400">{studySitesError}</div>
        {:else}
          <div class="space-y-2">
            <label class="text-sm text-gray-700 dark:text-gray-300" for="participant-site-select">
              Select a new site
            </label>
            <select
              id="participant-site-select"
              bind:value={selectedSiteForAssignment}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="" disabled>Select a site</option>
              {#each studySites as site (site._id)}
                <option value={site._id}>{getSiteLabel(site)}</option>
              {/each}
            </select>
          </div>
        {/if}

        <div class="space-y-2">
          <label class="text-sm text-gray-700 dark:text-gray-300" for="participant-site-confirm">
            Type "transfer" to confirm
          </label>
          <input
            id="participant-site-confirm"
            type="text"
            bind:value={siteAssignmentConfirmText}
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="transfer"
          />
        </div>

        {#if siteAssignmentError}
          <div class="text-sm text-red-600 dark:text-red-400">{siteAssignmentError}</div>
        {/if}
      </div>
      <div class="flex justify-end items-center gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          on:click={closeSiteAssignmentModal}
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          disabled={isSiteAssignmentSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          on:click={confirmSiteAssignmentChange}
          class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded"
          disabled={isSiteAssignmentSaving
            || isStudySitesLoading
            || !selectedSiteForAssignment
            || selectedSiteForAssignment === personToAssignSite?.siteGUID
            || siteAssignmentConfirmText.trim() !== 'transfer'}
        >
          {isSiteAssignmentSaving ? 'Updating...' : 'Confirm transfer'}
        </button>
      </div>
    </div>
  </div>
{/if}

<AuditLogModal
  showModal={showAuditLog}
  documentId={auditLogPerson?._id}
  collection="Subject"
  title={`Audit Logs - ${auditLogPerson?.firstName || ''} ${auditLogPerson?.lastName || 'Participant'}`}
  on:close={() => (showAuditLog = false)}
/>

<SiteForm
  showForm={showSiteForm}
  {editSite}
  on:submit={editSite ? handleEditSite : null}
  on:close={() => (showSiteForm = false)}
/>

<AuditLogModal
  showModal={showSiteAuditLog}
  documentId={auditLogSite?._id}
  collection="Site"
  title={`Audit Logs - ${auditLogSite?.siteName || 'Site'}`}
  on:close={() => (showSiteAuditLog = false)}
/>

{#if hasError}
  <div class="p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-100 rounded-lg mb-4">
    <p>An error occurred: {errorMessage}</p>
    <button 
      class="mt-2 px-4 py-2 bg-red-200 dark:bg-red-800 rounded hover:bg-red-300 dark:hover:bg-red-700"
      on:click={() => {
        hasError = false;
        errorMessage = '';
        if ($selectedSite) {
          fetchPersons(currentPage);
        }
      }}
    >
      Try Again
    </button>
  </div>
{/if}

<StudyHeader />

<FeatureInfo 
  title="Participants Management" 
  description="This page allows you to view and manage all participants for the selected site. You can search, create, edit, 
  and delete participants as well as export participant data to Excel. The header card shows information about the current site."
  marginBottom="mb-0"
/>

{#if $selectedSite}
  <SiteDetails 
    showOptionsMenu={true}
    {canViewAuditLogs}
    on:edit={(e) => {
      console.log('Edit site event received:', e.detail);
      editSite = e.detail;
      showSiteForm = true;
    }}
    on:auditLog={(e) => {
      console.log('Site audit log event received:', e.detail);
      auditLogSite = e.detail;
      showSiteAuditLog = true;
    }}
    on:siteChanged={(e) => {
      console.log('Site changed event received:', e.detail);
      // The selectedSite store is already updated by SiteDetails component
      // We just need to refresh the data for the new site
      fetchPersons(1);
      fetchAllPersons();
    }}
  />
{/if}

{#if isParticipantsCardViewEnabled}
  <PatientsInfoCards persons={allPersons} studyProperties={$studyProperties} loading={isLoadingKpi} />
{/if}

<Toolbar title="Participants" itemCountTitle="Participants" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="flex items-center gap-2">
      <div class="relative">
        <input
          type="text"
          placeholder="Search"
          bind:value={searchText}
          on:keydown={(e) => e.key === 'Enter' && handleSearch()}
          class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
        />
        {#if searchText}
          <button
            on:click={handleClearSearch}
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Clear search"
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </button>
        {/if}
      </div>
      <button on:click={handleSearch} title="Search">
        <FontAwesomeIcon icon={faSearch} />
      </button>
    </div>
    <button
      on:click={handleNewClick}
      class="pagination mx-1 me-2 p-0"
      disabled={!canAddPersons}
      class:opacity-50={!canAddPersons}
      title={!canAddPersons ? 'Insufficient permissions' : 'Add new participant'}
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  {#if isLoading}
    <div class="flex flex-col items-center justify-center py-12">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-600 dark:text-gray-400">Loading participants...</p>
    </div>
  {:else if persons.length > 0}
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Participant</th>
          <!-- <th class="px-4 py-2 text-left bold">Device</th> -->
          <th class="px-4 py-2 text-left bold">Latest Glucose (Local)</th>
          <!-- <th class="px-4 py-2 text-left bold">Asset Number</th> -->
          <th class="px-4 py-2 text-left bold">Active Sensor</th>
          <th class="px-4 py-2 text-left bold">Language</th>
          <th class="px-4 py-2 text-left bold">Timezone</th>
          <th class="px-4 py-2 text-left bold">Blinded</th>
          {#if isUpgradeEligibilityEnabled && canToggleEligibility}
            <th class="px-4 py-2 text-left bold">Eligible</th>
          {/if}
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="w-8 px-4 py-2 text-left bold">&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        {#each persons as person, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}
              >{person.subjectId}</td
            >
            <!-- <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}>
              {person.properties.enableddevice ? person.properties.enableddevice : 'Any'}
            </td> -->
            <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}>
              {#if person.latestReadingDate}
                <div class="flex items-center gap-2">
                  <div
                    class={`w-2 h-2 rounded-full border ${
                      isOlderThanHours(person.latestReadingDate, 24)
                        ? 'border-gray-400'
                        : isOlderThanHours(person.latestReadingDate, 3)
                          ? person.glucoseManufacturer === 'Abbott' ||
                            person.glucoseManufacturer === 'Dexcom'
                            ? 'bg-orange-500 border-orange-500'
                            : 'border-blue-500'
                          : person.glucoseManufacturer === 'Abbott' ||
                              person.glucoseManufacturer === 'Dexcom'
                            ? 'bg-green-500 border-green-500'
                            : 'bg-blue-500 border-blue-500'
                    }`}
                    title="Color coding: Green = Recent (<3h, Abbott/Dexcom), Blue = Recent (<3h, other), Orange = Older (3-24h, Abbott/Dexcom), Gray = Outdated (>24h)"
                  ></div>
                  <div>
                    <div class="text-xs">
                      {formatToLocalDateTime(person.latestReadingDate, person.timezoneCode)}
                    </div>
                    <!-- <div class="text-xs">
                      {person.glucoseManufacturer && person.glucoseModel
                        ? `${person.glucoseManufacturer} ${person.glucoseModel}`
                        : ''}
                    </div> -->
                  </div>
                </div>
              {/if}
            </td>
            <!-- <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}
              >{person.properties.assetnumber ? person.properties.assetnumber : ''}</td
            > -->
            <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}>
              {#if person.subjectActivities && person.subjectActivities.length > 0}
                {#each person.subjectActivities.filter(activity => activity.typeCode === 'MEAS' && activity.statusCode === 'START') as activity}
                  <div class="flex items-center gap-2">
                    <span class="text-xs">Yes</span>
                  </div>
                {/each}
                {#if person.subjectActivities.filter(activity => activity.typeCode === 'MEAS' && activity.statusCode === 'START').length === 0}
                  <span class="text-xs text-gray-500 dark:text-gray-400">No</span>
                {/if}
              {:else}
                <span class="text-xs text-gray-500 dark:text-gray-400">No</span>
              {/if}
            </td>
            <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}
              >{person.language ? person.flag + ' ' + person.language : ''}</td
            >
            <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}
              >{person.timezoneCode ? person.timezoneCode : ''}</td
            >
            <td class="px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}
              >{person.blinded ? 'Yes' : 'No'}</td
            >
            {#if isUpgradeEligibilityEnabled && canToggleEligibility}
              <td class="px-4 py-2">
                <span
                  class="inline-flex text-xs px-2 py-0.5 rounded-full ring-1 ring-inset {person.upgradeEligible ? 'text-green-600 ring-green-600' : 'text-gray-500 ring-gray-400'}"
                >
                  {person.upgradeEligible ? 'Yes' : 'No'}
                </span>
              </td>
            {/if}

            <td class="w-px px-4 py-2 cursor-pointer" on:click={() => handlePersonClick(person)}>
              <span
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                class:text-green-600={person.statusLabel === 'Screened/Enrolled'}
                class:ring-green-600={person.statusLabel === 'Screened/Enrolled'}
                class:text-green-500={person.statusLabel === 'Enrolled'}
                class:ring-green-500={person.statusLabel === 'Enrolled'}
                class:text-gray-600={person.statusLabel === 'Discontinued'}
                class:ring-gray-600={person.statusLabel === 'Discontinued'}
                class:text-gray-500={person.statusLabel === 'Screen Failed'}
                class:ring-gray-500={person.statusLabel === 'Screen Failed'}
                class:text-sky-600={person.statusLabel === 'Completed'}
                class:ring-sky-600={person.statusLabel === 'Completed'}
              >
                {person.statusLabel}
              </span>
            </td>
            <td class="w-px px-4 py-2">
              <div class="menu-container relative">
                <button
                  on:click={(e) => handlePersonMenuClick(e, person)}
                  class="pagination mx-1 p-0"
                  title="More options"
                >
                  <FontAwesomeIcon icon={faEllipsis} class="text-sky-800 dark:text-white/70" />
                </button>
                {#if showPersonMenu && selectedPersonForMenu?._id === person._id}
                  <div
                    class="fixed w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-[9999] menu-dropdown"
                    style="top: {personMenuPosition.top}px; left: {personMenuPosition.left}px; max-height: 200px; overflow-y: auto;"
                  >
                    <button
                      on:click={() => {
                        handleEditClick(person);
                        handlePersonMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canEditPersons}
                      class:opacity-50={!canEditPersons}
                    >
                      <span>Edit participant</span>
                      <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
                    </button>
                    <button
                      on:click={() => {
                        handleAuditLogClick(person);
                        handlePersonMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canViewAuditLogs}
                      class:opacity-50={!canViewAuditLogs}
                    >
                      <span>View audit logs</span>
                      <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
                    </button>
                    <button
                      on:click={() => {
                        handleDeleteClick(person);
                        handlePersonMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      disabled={!canDeletePersons}
                      class:opacity-50={!canDeletePersons}
                    >
                      <span>Delete participant</span>
                      <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                    </button>
                    {#if canAssignParticipantSite}
                      <button
                        on:click={() => {
                          openSiteAssignmentModal(person);
                          handlePersonMenuClose();
                        }}
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      >
                        <span>Transfer to site</span>
                        <FontAwesomeIcon icon={faCopy} class="text-sky-800 dark:text-white/70" />
                      </button>
                    {/if}
                  </div>
                {/if}
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <div class="text-center py-8 text-gray-500 dark:text-gray-400">
      No participants found
    </div>
  {/if}
</div>

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }

  .z-50 {
    z-index: 50;
  }

  .shadow-lg {
    --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color),
      0 4px 6px -4px var(--tw-shadow-color);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000),
      var(--tw-shadow);
  }

  .menu-dropdown {
    transition: none;
  }
</style>
